type BodyStyleSnapshot = {
  overflow: string;
  position: string;
  top: string;
  width: string;
  overscrollBehavior: string;
  htmlOverflow: string;
  htmlOverscrollBehavior: string;
  scrollTop: number;
};

const activeLockIds = new Map<string, number>();
let snapshot: BodyStyleSnapshot | null = null;
let modalBackdropObserver: MutationObserver | null = null;
let modalBackdropObserverConsumers = 0;
let releaseModalBackdropObserverLock: (() => void) | null = null;
let removeScrollEventListeners: (() => void) | null = null;
let lastTouchClientY: number | null = null;

const MODAL_BACKDROP_SELECTOR = '.modal-006-backdrop';
const MODAL_BACKDROP_CANDIDATE_SELECTOR = `${MODAL_BACKDROP_SELECTOR}, [data-modal-scroll-lock="true"]`;
const MODAL_BACKDROP_OBSERVER_LOCK_ID = 'modal-006-backdrop-observer';
const MODAL_CONTEXT_SELECTOR = '[role="dialog"], [aria-modal="true"], [data-modal-scroll-lock="true"], .modal-006-surface';

const canUseDom = (): boolean =>
  typeof document !== 'undefined' && typeof window !== 'undefined' && Boolean(document.body);

const getScrollParent = (target: EventTarget | null): HTMLElement | null => {
  if (!(target instanceof Element)) return null;

  const backdrop = target.closest(MODAL_BACKDROP_SELECTOR);
  if (!backdrop) return null;

  let element: Element | null = target;
  while (element && element !== backdrop) {
    if (element instanceof HTMLElement) {
      const style = window.getComputedStyle(element);
      const canOverflowY = /(auto|scroll|overlay)/.test(style.overflowY);
      if (canOverflowY && element.scrollHeight > element.clientHeight) {
        return element;
      }
    }
    element = element.parentElement;
  }

  return null;
};

const canScrollElement = (element: HTMLElement, deltaY: number): boolean => {
  if (deltaY < 0) return element.scrollTop > 0;
  if (deltaY > 0) return element.scrollTop + element.clientHeight < element.scrollHeight;
  return true;
};

const shouldAllowModalScroll = (target: EventTarget | null, deltaY: number): boolean => {
  if (!canUseDom()) return true;
  const scrollParent = getScrollParent(target);
  return Boolean(scrollParent && canScrollElement(scrollParent, deltaY));
};

const handleLockedWheel = (event: WheelEvent): void => {
  if (!shouldAllowModalScroll(event.target, event.deltaY)) {
    event.preventDefault();
  }
};

const handleLockedTouchStart = (event: TouchEvent): void => {
  lastTouchClientY = event.touches[0]?.clientY ?? null;
};

const handleLockedTouchMove = (event: TouchEvent): void => {
  const currentClientY = event.touches[0]?.clientY ?? null;
  const deltaY = lastTouchClientY === null || currentClientY === null
    ? 0
    : lastTouchClientY - currentClientY;

  if (!shouldAllowModalScroll(event.target, deltaY)) {
    event.preventDefault();
    return;
  }

  lastTouchClientY = currentClientY;
};

const addScrollEventListeners = (): void => {
  if (!canUseDom() || removeScrollEventListeners || typeof window.addEventListener !== 'function') return;

  const addOptions: AddEventListenerOptions = { passive: false, capture: true };
  const removeOptions: EventListenerOptions = { capture: true };
  window.addEventListener('wheel', handleLockedWheel, addOptions);
  window.addEventListener('touchstart', handleLockedTouchStart, addOptions);
  window.addEventListener('touchmove', handleLockedTouchMove, addOptions);
  removeScrollEventListeners = () => {
    window.removeEventListener('wheel', handleLockedWheel, removeOptions);
    window.removeEventListener('touchstart', handleLockedTouchStart, removeOptions);
    window.removeEventListener('touchmove', handleLockedTouchMove, removeOptions);
    removeScrollEventListeners = null;
    lastTouchClientY = null;
  };
};

const applyLock = (): void => {
  if (!canUseDom() || snapshot) return;

  const body = document.body;
  const html = document.documentElement;
  snapshot = {
    overflow: body.style.overflow,
    position: body.style.position,
    top: body.style.top,
    width: body.style.width,
    overscrollBehavior: body.style.overscrollBehavior,
    htmlOverflow: html?.style.overflow ?? '',
    htmlOverscrollBehavior: html?.style.overscrollBehavior ?? '',
    scrollTop: window.pageYOffset ?? window.scrollY ?? 0,
  };

  if (html) {
    html.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';
  }
  body.style.overflow = 'hidden';
  body.style.overscrollBehavior = 'none';
  body.style.position = 'fixed';
  body.style.top = `-${snapshot.scrollTop}px`;
  body.style.width = '100%';
  addScrollEventListeners();
};

const getActiveLockCount = (): number =>
  Array.from(activeLockIds.values()).reduce((total, count) => total + count, 0);

const releaseLock = (): void => {
  if (!canUseDom() || !snapshot || getActiveLockCount() > 0) return;

  const body = document.body;
  const html = document.documentElement;
  body.style.overflow = snapshot.overflow;
  body.style.position = snapshot.position;
  body.style.top = snapshot.top;
  body.style.width = snapshot.width;
  body.style.overscrollBehavior = snapshot.overscrollBehavior;
  if (html) {
    html.style.overflow = snapshot.htmlOverflow;
    html.style.overscrollBehavior = snapshot.htmlOverscrollBehavior;
  }
  removeScrollEventListeners?.();
  window.scrollTo(0, snapshot.scrollTop);
  snapshot = null;
};

export const acquireBodyScrollLock = (lockId: string): (() => void) => {
  const currentCount = activeLockIds.get(lockId) ?? 0;
  activeLockIds.set(lockId, currentCount + 1);
  applyLock();

  return () => {
    const activeCount = activeLockIds.get(lockId) ?? 0;
    if (activeCount <= 1) {
      activeLockIds.delete(lockId);
    } else {
      activeLockIds.set(lockId, activeCount - 1);
    }
    releaseLock();
  };
};

const isElementModalBackdrop = (element: Element): boolean => {
  if (element.getAttribute('data-modal-scroll-lock') === 'false') return false;
  if (element.matches(MODAL_CONTEXT_SELECTOR)) return true;
  return Boolean(element.querySelector(MODAL_CONTEXT_SELECTOR));
};

const hasOpenModalBackdropForScrollLock = (): boolean => {
  if (!canUseDom()) return false;
  const backdrops = Array.from(document.querySelectorAll(MODAL_BACKDROP_CANDIDATE_SELECTOR));
  return backdrops.some(isElementModalBackdrop);
};

const syncModalBackdropScrollLock = (): void => {
  if (!canUseDom()) return;

  const hasOpenModalBackdrop = hasOpenModalBackdropForScrollLock();
  if (hasOpenModalBackdrop && !releaseModalBackdropObserverLock) {
    releaseModalBackdropObserverLock = acquireBodyScrollLock(MODAL_BACKDROP_OBSERVER_LOCK_ID);
    return;
  }

  if (!hasOpenModalBackdrop && releaseModalBackdropObserverLock) {
    releaseModalBackdropObserverLock();
    releaseModalBackdropObserverLock = null;
  }
};

export const startModalBackdropScrollLockObserver = (): (() => void) => {
  if (!canUseDom() || typeof MutationObserver === 'undefined') {
    return () => undefined;
  }

  modalBackdropObserverConsumers += 1;
  if (!modalBackdropObserver) {
    modalBackdropObserver = new MutationObserver(syncModalBackdropScrollLock);
    modalBackdropObserver.observe(document.body, { childList: true, subtree: true });
    syncModalBackdropScrollLock();
  }

  return () => {
    modalBackdropObserverConsumers = Math.max(0, modalBackdropObserverConsumers - 1);
    if (modalBackdropObserverConsumers > 0) return;

    modalBackdropObserver?.disconnect();
    modalBackdropObserver = null;
    if (releaseModalBackdropObserverLock) {
      releaseModalBackdropObserverLock();
      releaseModalBackdropObserverLock = null;
    }
  };
};

export const isBodyScrollLocked = (): boolean => getActiveLockCount() > 0;

export const __resetBodyScrollLockForTests = (): void => {
  activeLockIds.clear();
  snapshot = null;
  modalBackdropObserver?.disconnect();
  modalBackdropObserver = null;
  modalBackdropObserverConsumers = 0;
  releaseModalBackdropObserverLock = null;
  removeScrollEventListeners?.();
};
